import { useMemo, useState } from "react";
import { usePersistentState } from "../../../hooks/usePersistentState";
import BackButton from "../../../components/navigation/BackButton";
import type { DurumTuru, AnimalItem } from "../../../mocks/animals";
import { useAnimals } from "../../../hooks/useAnimals";
import { useCustomers } from "../../../hooks/useCustomers";
import { usePayments } from "../../../hooks/usePayments";
import {
  analyzeAnimalDeletion,
  deleteAnimalCascade,
} from "../../../lib/cascadeOps";
import { readSessionUser } from "../../../lib/authSession";
import {
  getAnimalCustomerSearchText,
  getAnimalKgRefMode,
  getAnimalKgRefText,
  getAnimalMissingShareCount,
  getAnimalPaidTotal,
  getAnimalPaymentStatus,
  getAnimalRemainingBalance,
  getAnimalTotalCharge,
  getAnimalSaleStatus,
  getAnimalSalesType,
  getDisplayKupeNo,
  getDisplaySiparisNo,
  hasTransportForAnimal,
} from "../../../lib/animalLabels";
import { exportRowsToCsv, type ExportColumn } from "../../../lib/excelExport";
import { openAnimalSummaryPrintWindow } from "../../../lib/printTemplates";
import {
  matchesAnyOf,
  matchesContains,
  matchesExact,
  matchesNumericRange,
  uniqueFilterOptions,
  compareTextForSort,
  compareNumberForSort,
  applySortDirection,
  type NumericRangeFilter,
  type SortDirection,
} from "../../../lib/tableFilters";
import HeaderFilterCell, {
  FilterFooter,
  FilterOptionList,
  FilterRangeInputs,
  FilterSearchInput,
  FilterSortControls,
} from "../../../components/table/HeaderFilterCell";
import AnimalRow from "./components/AnimalRow";
import AddAnimalModal from "./components/AddAnimalModal";
import { ANIMAL_TABLE_MIN_WIDTH, ANIMAL_TABLE_TEMPLATE } from "./components/tableLayout";

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

interface AnimalHeaderFilters {
  kupeNo: string;
  siparisNo: string;
  tur: string[];
  satisTipi: string[];
  satisDurumu: string[];
  musteri: string;
  kgRefTipi: string[];
  satis: NumericRangeFilter;
  odenen: NumericRangeFilter;
  kalan: NumericRangeFilter;
  kalanDurumu: string[];
  kesimGunu: string[];
  tasima: string[];
  durum: string[];
  kesimeUygun: string[];
  odemeDurumu: string[];
  tamamlanan: string[];
}

const EMPTY_FILTERS: AnimalHeaderFilters = {
  kupeNo: "",
  siparisNo: "",
  tur: [],
  satisTipi: [],
  satisDurumu: [],
  musteri: "",
  kgRefTipi: [],
  satis: { min: "", max: "" },
  odenen: { min: "", max: "" },
  kalan: { min: "", max: "" },
  kalanDurumu: [],
  kesimGunu: [],
  tasima: [],
  durum: [],
  kesimeUygun: [],
  odemeDurumu: [],
  tamamlanan: [],
};

type AnimalSortKey =
  | "siraNo"
  | "kupeNo"
  | "siparisNo"
  | "tur"
  | "satisTipi"
  | "satisDurumu"
  | "musteri"
  | "kgRef"
  | "satis"
  | "odenen"
  | "kalan"
  | "kesimGunu"
  | "tasima"
  | "durum"
  | "kesimeUygun"
  | "odemeDurumu"
  | "tamamlanan";

function compareAnimalsByKey(a: AnimalItem, b: AnimalItem, key: AnimalSortKey) {
  if (key === "siraNo") return compareNumberForSort(a.siraNo, b.siraNo);
  if (key === "kupeNo") return compareTextForSort(getDisplayKupeNo(a), getDisplayKupeNo(b));
  if (key === "siparisNo") return compareTextForSort(getDisplaySiparisNo(a), getDisplaySiparisNo(b));
  if (key === "tur") return compareTextForSort(a.tur, b.tur);
  if (key === "satisTipi") return compareTextForSort(getAnimalSalesType(a), getAnimalSalesType(b));
  if (key === "satisDurumu") return compareTextForSort(getAnimalSaleStatus(a), getAnimalSaleStatus(b));
  if (key === "musteri") return compareTextForSort(getAnimalCustomerSearchText(a), getAnimalCustomerSearchText(b));
  if (key === "kgRef") return compareTextForSort(getAnimalKgRefText(a), getAnimalKgRefText(b));
  if (key === "satis") return compareNumberForSort(getAnimalTotalCharge(a), getAnimalTotalCharge(b));
  if (key === "odenen") return compareNumberForSort(getAnimalPaidTotal(a), getAnimalPaidTotal(b));
  if (key === "kalan") return compareNumberForSort(getAnimalRemainingBalance(a), getAnimalRemainingBalance(b));
  if (key === "kesimGunu") return compareTextForSort(a.kesimGunu, b.kesimGunu);
  if (key === "tasima") return compareTextForSort(hasTransportForAnimal(a) ? "Taşıma Var" : "Taşıma Yok", hasTransportForAnimal(b) ? "Taşıma Var" : "Taşıma Yok");
  if (key === "durum") return compareTextForSort(a.durum, b.durum);
  if (key === "kesimeUygun") return compareTextForSort(a.kesimeUygun ? "Evet" : "Hayır", b.kesimeUygun ? "Evet" : "Hayır");
  if (key === "odemeDurumu") return compareTextForSort(getAnimalPaymentStatus(a), getAnimalPaymentStatus(b));
  if (key === "tamamlanan") return compareTextForSort(a.tamamlandi ? "Evet" : "Hayır", b.tamamlandi ? "Evet" : "Hayır");
  return 0;
}

const ANIMAL_SORT_LABELS: Record<AnimalSortKey, string> = {
  siraNo: "Sıra No",
  kupeNo: "Küpe No",
  siparisNo: "Sipariş No",
  tur: "Büyükbaş/Küçükbaş",
  satisTipi: "Hisseli",
  satisDurumu: "Satış Durumu",
  musteri: "Müşteri",
  kgRef: "KG/REF Fiyat",
  satis: "Satış Tutarı",
  odenen: "Ödenen",
  kalan: "Kalan",
  kesimGunu: "Kesim Günü",
  tasima: "Taşıma",
  durum: "Kesim Durumu",
  kesimeUygun: "Kesime Uygun",
  odemeDurumu: "Ödeme Durumu",
  tamamlanan: "Tamamlanan",
};

interface ExportAnimalRow {
  kupeNo: string;
  siparisNo: string;
  tur: string;
  satisTipi: string;
  satisDurumu: string;
  musteri: string;
  kgRef: string;
  satis: number;
  odenen: number;
  kalan: number;
  kesimGunu: string;
  tasima: string;
  kesimDurum: string;
  kesimeUygun: string;
  odemeDurumu: string;
  tamamlanan: string;
  toplamHisse: number;
  satilanHisse: number;
  eksikHisse: number;
}

function buildSafeAnimal(item: AnimalItem): AnimalItem {
  return {
    ...item,
    siraNo: item.siraNo ?? 0,
    kupeNo: item.kupeNo ?? "",
    tur: item.tur ?? "Büyükbaş",
    toplamHisse: item.toplamHisse ?? 0,
    satirlanHisse: item.satirlanHisse ?? 0,
    toplamSatisTutari: item.toplamSatisTutari ?? 0,
    toplamOdenen: item.toplamOdenen ?? 0,
    durum: (item.durum ?? "Aktif") as DurumTuru,
    kesimeUygun: Boolean(item.kesimeUygun),
    tamamlandi: Boolean(item.tamamlandi),
    disKesim: Boolean(item.disKesim),
    hisseliSatisEtiketi:
      typeof item.hisseliSatisEtiketi === "boolean"
        ? item.hisseliSatisEtiketi
        : (item.toplamHisse ?? 0) > 1,
    hisseler: Array.isArray(item.hisseler) ? item.hisseler : [],
  };
}

function toggleSelection(list: string[], value: string) {
  return list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];
}

export default function HayvanlarPage() {
  const { animals: animalData, setAllAnimals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { payments, setAllPayments } = usePayments();

  const [filters, setFilters] = usePersistentState<AnimalHeaderFilters>("ec.filters.hayvanlar.v2", EMPTY_FILTERS);
  const [sortKey, setSortKey] = usePersistentState<AnimalSortKey>("ec.sort.hayvanlar.key.v1", "siraNo");
  const [sortDir, setSortDir] = usePersistentState<SortDirection>("ec.sort.hayvanlar.dir.v1", "asc");
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const sessionUser = readSessionUser();
  const isAdmin = sessionUser?.rol === "Admin";

  const safeAnimals = useMemo(
    () => (animalData ?? []).map(buildSafeAnimal).sort((a, b) => a.siraNo - b.siraNo),
    [animalData]
  );

  const kesimGunuOptions = useMemo(
    () => uniqueFilterOptions(safeAnimals.map((animal) => animal.kesimGunu)),
    [safeAnimals]
  );

  const filtered = useMemo(() => {
    return safeAnimals.filter((animal) => {
      const displayKupeNo = getDisplayKupeNo(animal);
      const displaySiparisNo = getDisplaySiparisNo(animal);
      const tur = animal.tur ?? "Büyükbaş";
      const satisTipi = getAnimalSalesType(animal);
      const satisDurumu = getAnimalSaleStatus(animal);
      const musteri = getAnimalCustomerSearchText(animal);
      const kgRefTipi = getAnimalKgRefMode(animal);
      const kalanTutar = getAnimalRemainingBalance(animal);
      const kalanDurumu = kalanTutar > 0 ? "Eksik" : kalanTutar < 0 ? "Fazla" : "Tamamlanan";
      const kesimGunu = animal.kesimGunu ?? "";
      const tasima = hasTransportForAnimal(animal) ? "Taşıma Var" : "Taşıma Yok";
      const kesimeUygun = animal.kesimeUygun ? "Evet" : "Hayır";
      const odemeDurumu = getAnimalPaymentStatus(animal);
      const tamamlanan = animal.tamamlandi ? "Evet" : "Hayır";

      if (!matchesExact(displayKupeNo, filters.kupeNo)) return false;
      if (!matchesExact(displaySiparisNo, filters.siparisNo)) return false;
      if (!matchesAnyOf(tur, filters.tur ?? [])) return false;
      if (!matchesAnyOf(satisTipi, filters.satisTipi)) return false;
      if (!matchesAnyOf(satisDurumu, filters.satisDurumu)) return false;
      if (!matchesContains(musteri, filters.musteri)) return false;
      if (!matchesAnyOf(kgRefTipi, filters.kgRefTipi)) return false;
      if (!matchesNumericRange(getAnimalTotalCharge(animal), filters.satis)) return false;
      if (!matchesNumericRange(getAnimalPaidTotal(animal), filters.odenen)) return false;
      if (!matchesNumericRange(kalanTutar, filters.kalan)) return false;
      if (!matchesAnyOf(kalanDurumu, filters.kalanDurumu ?? [])) return false;
      if (!matchesAnyOf(kesimGunu, filters.kesimGunu)) return false;
      if (!matchesAnyOf(tasima, filters.tasima)) return false;
      if (!matchesAnyOf(animal.durum, filters.durum)) return false;
      if (!matchesAnyOf(kesimeUygun, filters.kesimeUygun)) return false;
      if (!matchesAnyOf(odemeDurumu, filters.odemeDurumu ?? [])) return false;
      if (!matchesAnyOf(tamamlanan, filters.tamamlanan)) return false;

      return true;
    }).sort((a, b) => applySortDirection(compareAnimalsByKey(a, b, sortKey), sortDir));
  }, [filters, safeAnimals, sortDir, sortKey]);

  const summary = useMemo(() => {
    const totalSatis = filtered.reduce((sum, animal) => sum + getAnimalTotalCharge(animal), 0);
    const totalOdenen = filtered.reduce((sum, animal) => sum + getAnimalPaidTotal(animal), 0);
    const totalKalan = filtered.reduce(
      (sum, animal) => sum + getAnimalRemainingBalance(animal),
      0
    );

    return {
      totalHayvan: filtered.length,
      totalSatis,
      totalOdenen,
      totalKalan,
    };
  }, [filtered]);

  function exportCurrent() {
    const rows: ExportAnimalRow[] = filtered.map((animal) => ({
      kupeNo: getDisplayKupeNo(animal),
      siparisNo: getDisplaySiparisNo(animal),
      tur: animal.tur,
      satisTipi: getAnimalSalesType(animal),
      satisDurumu: getAnimalSaleStatus(animal),
      musteri: getAnimalCustomerSearchText(animal),
      kgRef: getAnimalKgRefText(animal),
      kesimGunu: animal.kesimGunu ?? "",
      tasima: hasTransportForAnimal(animal) ? "Taşıma Var" : "Taşıma Yok",
      kesimDurum: animal.durum,
      kesimeUygun: animal.kesimeUygun ? "Evet" : "Hayır",
      odemeDurumu: getAnimalPaymentStatus(animal),
      tamamlanan: animal.tamamlandi ? "Evet" : "Hayır",
      toplamHisse: animal.toplamHisse,
      satilanHisse: animal.satirlanHisse,
      eksikHisse: getAnimalMissingShareCount(animal),
      satis: getAnimalTotalCharge(animal),
      odenen: getAnimalPaidTotal(animal),
      kalan: getAnimalRemainingBalance(animal),
    }));

    const columns: ExportColumn<ExportAnimalRow>[] = [
      { key: "kupeNo", label: "Küpe No" },
      { key: "siparisNo", label: "Sipariş No" },
      { key: "tur", label: "Büyükbaş/Küçükbaş" },
      { key: "satisTipi", label: "Hisseli / Dış Kesim" },
      { key: "satisDurumu", label: "Satış Durumu" },
      { key: "musteri", label: "Müşteri" },
      { key: "kgRef", label: "KG/REF Fiyat" },
      { key: "satis", label: "Satış" },
      { key: "odenen", label: "Ödenen" },
      { key: "kalan", label: "Kalan" },
      { key: "kesimGunu", label: "Kesim Günü" },
      { key: "tasima", label: "Taşıma" },
      { key: "kesimDurum", label: "Kesim Durumu" },
      { key: "kesimeUygun", label: "Kesime Uygun" },
      { key: "odemeDurumu", label: "Ödeme Durumu" },
      { key: "tamamlanan", label: "Tamamlanan" },
      { key: "toplamHisse", label: "Toplam Hisse" },
      { key: "satilanHisse", label: "Satılan Hisse" },
      { key: "eksikHisse", label: "Eksik Hisse" },
    ];

    exportRowsToCsv(
      `hayvanlar-${new Date().toISOString().slice(0, 10)}`,
      rows,
      columns
    );
  }

  const toggleKesimeUygun = (id: number) => {
    const target = safeAnimals.find((item) => item.id === id);
    if (!target) return;

    const nextKesimValue = !target.kesimeUygun;
    const kalanTutar = getAnimalRemainingBalance(target);

    if (nextKesimValue && kalanTutar > 0 && !isAdmin) {
      window.alert(
        "Ödemesi tamamlanmamış hayvanı manuel olarak kesime sadece Admin gönderebilir."
      );
      return;
    }

    void setAllAnimals((prev) =>
      prev.map((animal) => (animal.id === id ? { ...animal, kesimeUygun: nextKesimValue } : animal))
    );
  };

  const toggleTamamlandi = (id: number) => {
    void setAllAnimals((prev) =>
      prev.map((animal) =>
        animal.id === id ? { ...animal, tamamlandi: !animal.tamamlandi } : animal
      )
    );
  };

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const handleDeleteAnimal = (id: number) => {
    const analysis = analyzeAnimalDeletion(
      id,
      animalData ?? [],
      payments ?? [],
      customers ?? []
    );

    if (!analysis.animal) {
      window.alert("Silinecek hayvan bulunamadı.");
      return;
    }

    const ok = window.confirm(
      [
        `Küpe no ${getDisplayKupeNo(analysis.animal)} olan hayvan silinsin mi?`,
        `Sipariş no: ${getDisplaySiparisNo(analysis.animal)}`,
        "",
        `Bağlı hisse: ${analysis.shareCount}`,
        `Bağlı ödeme kaydı: ${analysis.paymentCount}`,
        `Etkilenecek müşteri: ${analysis.affectedCustomerCount}`,
        "",
        "Bu işlem geri alınamaz.",
      ].join("\n")
    );

    if (!ok) return;

    const result = deleteAnimalCascade({
      animalId: id,
      animals: animalData ?? [],
      customers: customers ?? [],
      payments: payments ?? [],
    });

    void setAllAnimals(() => result.animals);
    void setAllCustomers(result.customers);
    void setAllPayments(() => result.payments);
  };

  function handlePrintAnimal(animal: AnimalItem) {
    openAnimalSummaryPrintWindow(animal);
  }

  function renderSortControls(key: AnimalSortKey, ascLabel?: string, descLabel?: string) {
    return (
      <FilterSortControls
        activeDirection={sortKey === key ? sortDir : null}
        ascLabel={ascLabel}
        descLabel={descLabel}
        onSort={(direction) => {
          setSortKey(key);
          setSortDir(direction);
        }}
      />
    );
  }

  const hasActiveFilters =
    Boolean(filters.kupeNo) ||
    Boolean(filters.siparisNo) ||
    (filters.tur ?? []).length > 0 ||
    filters.satisTipi.length > 0 ||
    filters.satisDurumu.length > 0 ||
    Boolean(filters.musteri) ||
    filters.kgRefTipi.length > 0 ||
    Boolean(filters.satis.min || filters.satis.max) ||
    Boolean(filters.odenen.min || filters.odenen.max) ||
    Boolean(filters.kalan.min || filters.kalan.max) ||
    (filters.kalanDurumu ?? []).length > 0 ||
    filters.kesimGunu.length > 0 ||
    filters.tasima.length > 0 ||
    filters.durum.length > 0 ||
    filters.kesimeUygun.length > 0 ||
    (filters.odemeDurumu ?? []).length > 0 ||
    filters.tamamlanan.length > 0;

  return (
    <div className="space-y-3">
      <div className="lg:sticky lg:top-0 z-40 -mx-3 border-b border-farm-border/70 bg-farm-cream/95 px-3 pb-3 pt-1 shadow-sm backdrop-blur">
        <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-xl font-bold text-farm-brown">Hayvanlar</h1>
          <p className="text-sm text-farm-muted mt-1">
            Başlığa basarak Excel gibi filtreleyin. Küpe ve sipariş numarası tam eşleşir.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <BackButton />
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={() => setFilters(EMPTY_FILTERS)}
              className="h-10 px-4 rounded-full border border-farm-border bg-white text-farm-muted text-sm font-semibold hover:bg-farm-cream"
            >
              Filtreleri Temizle
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setMobileFiltersOpen((prev) => !prev)}
            className="lg:hidden h-10 px-4 rounded-full border border-farm-border bg-white text-farm-brown text-sm font-semibold hover:bg-farm-cream flex items-center gap-2"
          >
            <i className="ri-filter-3-line text-base" />
            Mobil Filtre
          </button>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="h-10 px-4 rounded-full bg-farm-earth text-white text-sm font-semibold hover:bg-farm-brown flex items-center gap-2"
          >
            <i className="ri-add-line text-base" />
            Yeni Hayvan
          </button>
          <button
            type="button"
            onClick={exportCurrent}
            className="h-10 px-4 rounded-full border border-farm-border bg-white text-farm-brown text-sm font-semibold hover:bg-farm-cream flex items-center gap-2"
          >
            <i className="ri-file-excel-2-line text-base" />
            Excel Çıktısı
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <SummaryCard icon="ri-database-2-line" label="Toplam Hayvan" value={String(summary.totalHayvan)} />
        <SummaryCard icon="ri-coins-line" label="Toplam Satış" value={fmt(summary.totalSatis)} />
        <SummaryCard icon="ri-check-double-line" label="Toplam Ödenen" value={fmt(summary.totalOdenen)} accent="text-farm-green" />
        <SummaryCard icon="ri-time-line" label="Toplam Kalan" value={fmt(summary.totalKalan)} accent="text-farm-earth" />
      </div>

      {mobileFiltersOpen && (
        <div className="lg:hidden rounded-farm border border-farm-border bg-white p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              value={filters.kupeNo}
              onChange={(e) => setFilters((prev) => ({ ...prev, kupeNo: e.target.value }))}
              placeholder="Tam küpe no"
              className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth"
            />
            <input
              value={filters.siparisNo}
              onChange={(e) => setFilters((prev) => ({ ...prev, siparisNo: e.target.value }))}
              placeholder="Tam sipariş no"
              className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth"
            />
            <input
              value={filters.musteri}
              onChange={(e) => setFilters((prev) => ({ ...prev, musteri: e.target.value }))}
              placeholder="Müşteri adı / telefon"
              className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth"
            />
            <select
              value={filters.satisDurumu[0] ?? ""}
              onChange={(e) => setFilters((prev) => ({ ...prev, satisDurumu: e.target.value ? [e.target.value] : [] }))}
              className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth"
            >
              <option value="">Satış durumu: Tümü</option>
              <option value="SATILDI">SATILDI</option>
              <option value="DEVAM">DEVAM</option>
              <option value="SATIŞ YOK">SATIŞ YOK</option>
            </select>
            <select
              value={filters.satisTipi[0] ?? ""}
              onChange={(e) => setFilters((prev) => ({ ...prev, satisTipi: e.target.value ? [e.target.value] : [] }))}
              className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth"
            >
              <option value="">Satış tipi: Tümü</option>
              <option value="Hisseli">Hisseli</option>
              <option value="Hissesiz">Hissesiz</option>
              <option value="Dış Kesim">Dış Kesim</option>
            </select>
            <select
              value={(filters.kalanDurumu ?? [])[0] ?? ""}
              onChange={(e) => setFilters((prev) => ({ ...prev, kalanDurumu: e.target.value ? [e.target.value] : [] }))}
              className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth"
            >
              <option value="">Kalan tutar: Tümü</option>
              <option value="Eksik">Eksik</option>
              <option value="Tamamlanan">Tamamlanan</option>
              <option value="Fazla">Fazla</option>
            </select>
            <select
              value={filters.kesimeUygun[0] ?? ""}
              onChange={(e) => setFilters((prev) => ({ ...prev, kesimeUygun: e.target.value ? [e.target.value] : [] }))}
              className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth"
            >
              <option value="">Kesime uygun: Tümü</option>
              <option value="Evet">Evet</option>
              <option value="Hayır">Hayır</option>
            </select>
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as AnimalSortKey)}
              className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth"
            >
              {(Object.keys(ANIMAL_SORT_LABELS) as AnimalSortKey[]).map((key) => (
                <option key={key} value={key}>Sırala: {ANIMAL_SORT_LABELS[key]}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))}
              className="h-10 rounded-xl border border-farm-border bg-white px-3 text-sm font-semibold text-farm-brown"
            >
              {sortDir === "asc" ? "Artan" : "Azalan"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setFilters(EMPTY_FILTERS)}
            className="w-full h-10 rounded-xl border border-farm-border text-sm font-semibold text-farm-muted"
          >
            Filtreleri Temizle
          </button>
        </div>
      )}

      <div className="rounded-farm border border-farm-border bg-white p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <FilterSearchInput
            value={filters.kupeNo}
            onChange={(value) => setFilters((prev) => ({ ...prev, kupeNo: value }))}
            placeholder="Küpe No ara"
          />
          <FilterSearchInput
            value={filters.siparisNo}
            onChange={(value) => setFilters((prev) => ({ ...prev, siparisNo: value }))}
            placeholder="Sipariş No ara"
          />
          <FilterSearchInput
            value={filters.musteri}
            onChange={(value) => setFilters((prev) => ({ ...prev, musteri: value }))}
            placeholder="Ad Soyad ara"
          />
        </div>
      </div>

        <div className="rounded-t-farm bg-farm-warm border border-farm-border border-b-0 overflow-x-auto overflow-y-visible">
          <div className="min-w-full">
            <div
              className="hidden bg-farm-cream lg:grid w-full items-center gap-1 px-2 border-b border-farm-border text-[10px] font-body font-semibold text-farm-muted uppercase tracking-wide shadow-sm"
              style={{
                gridTemplateColumns: ANIMAL_TABLE_TEMPLATE,
                minWidth: `${ANIMAL_TABLE_MIN_WIDTH}px`,
              }}
            >
            <HeaderFilterCell label="Küpe No" active={Boolean(filters.kupeNo) || sortKey === "kupeNo"}>
              {renderSortControls("kupeNo")}
              <FilterSearchInput
                value={filters.kupeNo}
                onChange={(value) => setFilters((prev) => ({ ...prev, kupeNo: value }))}
                placeholder="Tam küpe no"
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, kupeNo: "" }))} hint="Tam eşleşme" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Sipariş No" active={Boolean(filters.siparisNo) || sortKey === "siparisNo"}>
              {renderSortControls("siparisNo")}
              <FilterSearchInput
                value={filters.siparisNo}
                onChange={(value) => setFilters((prev) => ({ ...prev, siparisNo: value }))}
                placeholder="Tam sipariş no"
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, siparisNo: "" }))} hint="Tam eşleşme" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Büyükbaş / Küçükbaş" active={(filters.tur ?? []).length > 0 || sortKey === "tur"}>
              {renderSortControls("tur")}
              <FilterOptionList
                options={["Büyükbaş", "Küçükbaş"]}
                selected={filters.tur ?? []}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, tur: toggleSelection(prev.tur ?? [], value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, tur: [] }))} hint="Büyükbaş / Küçükbaş / Hepsi" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Hisseli" active={filters.satisTipi.length > 0 || sortKey === "satisTipi"}>
              {renderSortControls("satisTipi")}
              <FilterOptionList
                options={["Hisseli", "Hissesiz", "Dış Kesim"]}
                selected={filters.satisTipi}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, satisTipi: toggleSelection(prev.satisTipi, value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, satisTipi: [] }))} hint="Hisseli / Hissesiz / Dış Kesim" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Satış Durumu" active={filters.satisDurumu.length > 0 || sortKey === "satisDurumu"}>
              {renderSortControls("satisDurumu")}
              <FilterOptionList
                options={["SATILDI", "DEVAM", "SATIŞ YOK"]}
                selected={filters.satisDurumu}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, satisDurumu: toggleSelection(prev.satisDurumu, value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, satisDurumu: [] }))} />
            </HeaderFilterCell>

            <HeaderFilterCell label="Müşteri" active={Boolean(filters.musteri) || sortKey === "musteri"}>
              {renderSortControls("musteri")}
              <FilterSearchInput
                value={filters.musteri}
                onChange={(value) => setFilters((prev) => ({ ...prev, musteri: value }))}
                placeholder="Ad veya telefon"
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, musteri: "" }))} hint="İçerir araması" />
            </HeaderFilterCell>

            <HeaderFilterCell label="KG/REF Fiyat" active={filters.kgRefTipi.length > 0 || sortKey === "kgRef"}>
              {renderSortControls("kgRef")}
              <FilterOptionList
                options={["KG Satış", "Normal Satış"]}
                selected={filters.kgRefTipi}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, kgRefTipi: toggleSelection(prev.kgRefTipi, value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, kgRefTipi: [] }))} hint="KG bilgisi olanlar / olmayanlar" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Satış Tutarı" active={Boolean(filters.satis.min || filters.satis.max) || sortKey === "satis"} align="right">
              {renderSortControls("satis", "Küçükten büyüğe", "Büyükten küçüğe")}
              <FilterRangeInputs
                min={filters.satis.min}
                max={filters.satis.max}
                onMinChange={(value) => setFilters((prev) => ({ ...prev, satis: { ...prev.satis, min: value } }))}
                onMaxChange={(value) => setFilters((prev) => ({ ...prev, satis: { ...prev.satis, max: value } }))}
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, satis: { min: "", max: "" } }))} hint="Min - Max aralığı" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Ödenen" active={Boolean(filters.odenen.min || filters.odenen.max) || sortKey === "odenen"} align="right">
              {renderSortControls("odenen", "Küçükten büyüğe", "Büyükten küçüğe")}
              <FilterRangeInputs
                min={filters.odenen.min}
                max={filters.odenen.max}
                onMinChange={(value) => setFilters((prev) => ({ ...prev, odenen: { ...prev.odenen, min: value } }))}
                onMaxChange={(value) => setFilters((prev) => ({ ...prev, odenen: { ...prev.odenen, max: value } }))}
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, odenen: { min: "", max: "" } }))} hint="Min - Max aralığı" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Kalan" active={Boolean(filters.kalan.min || filters.kalan.max) || (filters.kalanDurumu ?? []).length > 0 || sortKey === "kalan"} align="right">
              {renderSortControls("kalan", "Küçükten büyüğe", "Büyükten küçüğe")}
              <FilterOptionList
                options={["Eksik", "Tamamlanan", "Fazla"]}
                selected={filters.kalanDurumu ?? []}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, kalanDurumu: toggleSelection(prev.kalanDurumu ?? [], value) }))
                }
              />
              <div className="my-2 border-t border-farm-border" />
              <FilterRangeInputs
                min={filters.kalan.min}
                max={filters.kalan.max}
                onMinChange={(value) => setFilters((prev) => ({ ...prev, kalan: { ...prev.kalan, min: value } }))}
                onMaxChange={(value) => setFilters((prev) => ({ ...prev, kalan: { ...prev.kalan, max: value } }))}
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, kalan: { min: "", max: "" }, kalanDurumu: [] }))} hint="Eksik > 0 · Tamamlanan = 0 · Fazla < 0" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Kesim Günü" active={filters.kesimGunu.length > 0 || sortKey === "kesimGunu"}>
              {renderSortControls("kesimGunu", "Eskiden yeniye", "Yeniden eskiye")}
              <FilterOptionList
                options={kesimGunuOptions}
                selected={filters.kesimGunu}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, kesimGunu: toggleSelection(prev.kesimGunu, value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, kesimGunu: [] }))} />
            </HeaderFilterCell>

            <HeaderFilterCell label="Taşıma" active={filters.tasima.length > 0 || sortKey === "tasima"}>
              {renderSortControls("tasima")}
              <FilterOptionList
                options={["Taşıma Var", "Taşıma Yok"]}
                selected={filters.tasima}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, tasima: toggleSelection(prev.tasima, value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, tasima: [] }))} />
            </HeaderFilterCell>

            <HeaderFilterCell label="Kesim Durumu" active={filters.durum.length > 0 || sortKey === "durum"}>
              {renderSortControls("durum")}
              <FilterOptionList
                options={["Aktif", "Kesim Bekliyor", "Kesildi", "Teslim Edildi"]}
                selected={filters.durum}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, durum: toggleSelection(prev.durum, value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, durum: [] }))} />
            </HeaderFilterCell>

            <HeaderFilterCell label="Kesime Uygun" active={filters.kesimeUygun.length > 0 || sortKey === "kesimeUygun"}>
              {renderSortControls("kesimeUygun")}
              <FilterOptionList
                options={["Evet", "Hayır"]}
                selected={filters.kesimeUygun}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, kesimeUygun: toggleSelection(prev.kesimeUygun, value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, kesimeUygun: [] }))} />
            </HeaderFilterCell>

            <HeaderFilterCell label="Ödeme Durumu" active={(filters.odemeDurumu ?? []).length > 0 || sortKey === "odemeDurumu"}>
              {renderSortControls("odemeDurumu")}
              <FilterOptionList
                options={["Tamamlanan", "Bekleyen"]}
                selected={filters.odemeDurumu ?? []}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, odemeDurumu: toggleSelection(prev.odemeDurumu ?? [], value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, odemeDurumu: [] }))} hint="Tümü için temizle" />
            </HeaderFilterCell>

            <HeaderFilterCell label="Tamamlanan" active={filters.tamamlanan.length > 0 || sortKey === "tamamlanan"}>
              {renderSortControls("tamamlanan")}
              <FilterOptionList
                options={["Evet", "Hayır"]}
                selected={filters.tamamlanan}
                onToggle={(value) =>
                  setFilters((prev) => ({ ...prev, tamamlanan: toggleSelection(prev.tamamlanan, value) }))
                }
              />
              <FilterFooter onClear={() => setFilters((prev) => ({ ...prev, tamamlanan: [] }))} />
            </HeaderFilterCell>

              <div className="px-2 py-2 text-right">İşlem</div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-b-farm bg-farm-warm border border-farm-border border-t-0 overflow-x-auto overflow-y-visible">
        <div className="min-w-full">
          {filtered.length === 0 ? (
            <div className="bg-white px-4 py-10 text-center text-farm-muted">
              Filtreye uyan hayvan yok.
            </div>
          ) : (
            filtered.map((animal) => (
              <AnimalRow
                key={animal.id}
                animal={animal}
                expanded={expandedIds.includes(animal.id)}
                onToggleExpand={() => toggleExpand(animal.id)}
                onToggleKesimeUygun={toggleKesimeUygun}
                onToggleTamamlandi={toggleTamamlandi}
                onDelete={handleDeleteAnimal}
                onPrint={handlePrintAnimal}
              />
            ))
          )}
        </div>
      </div>

      {showAddModal ? <AddAnimalModal onClose={() => setShowAddModal(false)} /> : null}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  accent,
}: {
  icon: string;
  label: string;
  value: string;
  accent?: string;
}) {
  return (
    <div className="rounded-farm border border-farm-border bg-farm-warm px-4 py-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-2xl flex items-center justify-center bg-farm-cream ${accent ?? "text-farm-earth"}`}>
          <i className={`${icon} text-lg`} />
        </div>
        <div>
          <p className="text-[11px] text-farm-muted uppercase tracking-wide">{label}</p>
          <p className={`text-base font-semibold ${accent ?? "text-farm-brown"}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}
